import * as React from 'react';
import {
  Avatar,
  AvatarGroup,
  Box,
  Button,
  Chip,
  ListItemIcon,
  ListItemText,
  Menu,
  MenuItem,
  Stack,
  Tooltip,
  Typography,
} from '@mui/material';
import {
  Download as DownloadIcon,
  FileCsv as CsvIcon,
  FileXls as ExcelIcon,
  HouseLine as HouseLineIcon,
  MapPin as MapPinIcon,
} from '@phosphor-icons/react/dist/ssr';
import {
  MaterialReactTable,
  MRT_GlobalFilterTextField,
  MRT_ShowHideColumnsButton,
  MRT_ToggleDensePaddingButton,
  MRT_ToggleFiltersButton,
  useMaterialReactTable,
  type MRT_ColumnDef,
} from 'material-react-table';
import { useNavigate } from 'react-router-dom';
import * as XLSX from 'xlsx';
import dayjs from 'dayjs';

import type { Unit } from '@/types/v2/unit';
import { paths } from '@/paths';

interface PaginationMeta {
  per_page: number;
  current_page: number;
  has_more: boolean;
  next_page: string | null;
  prev_page: string | null;
  total: number;
  total_pages: number;
}

interface UnitsTableProps {
  rows: Unit[];
  loading: boolean;
  onSuccess: (unit: Unit) => void;
  onDeleteSuccess: (id: string) => void;
  onBulkDeleteSuccess?: () => void;
  pagination?: PaginationMeta;
  onPageChange?: (page: number) => void;
  onRowsPerPageChange?: (rowsPerPage: number) => void;
}

export function UnitsTable({ rows, loading }: UnitsTableProps): React.JSX.Element {
  const navigate = useNavigate();
  const [exportMenuAnchor, setExportMenuAnchor] = React.useState<null | HTMLElement>(null);

  const formatRow = (unit: Unit) => {
    const residents = unit.current_residents && unit.current_residents.length > 0 ? unit.current_residents : (unit.owners ?? []);
    return {
      Address: unit.address ?? '',
      'Unit Number': unit.unit_number ?? '',
      City: unit.city ?? '',
      State: unit.state ?? '',
      'Zip Code': unit.zip_code ?? '',
      Floor: unit.floor ?? '',
      Rental: unit.is_rental ? 'Yes' : 'No',
      Residents: residents.length,
      Created: dayjs(unit.created_at).format('MMM D, YYYY'),
    };
  };

  const exportToCSV = () => {
    const ws = XLSX.utils.json_to_sheet(rows.map(formatRow));
    const wb = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(wb, ws, 'Units');
    XLSX.writeFile(wb, `units_${dayjs().format('YYYY-MM-DD')}.csv`);
    setExportMenuAnchor(null);
  };

  const exportToExcel = () => {
    const ws = XLSX.utils.json_to_sheet(rows.map(formatRow));
    const wb = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(wb, ws, 'Units');
    XLSX.writeFile(wb, `units_${dayjs().format('YYYY-MM-DD')}.xlsx`);
    setExportMenuAnchor(null);
  };

  const columns = React.useMemo<MRT_ColumnDef<Unit>[]>(
    () => [
      {
        accessorKey: 'address',
        header: 'Address',
        size: 300,
        grow: true,
        enableSorting: true,
        enableColumnFilter: true,
        filterVariant: 'text',
        accessorFn: (row) => row.address ?? '',
        Cell: ({ row }) => (
          <Box>
            <Typography sx={{ fontWeight: 600, fontSize: '0.875rem' }} variant="body2">
              {row.original.address || 'No address'}
              {row.original.unit_number ? ` — Unit ${row.original.unit_number}` : ''}
            </Typography>
            {(row.original.city || row.original.state) && (
              <Stack direction="row" spacing={0.5} sx={{ alignItems: 'center', mt: 0.25 }}>
                <MapPinIcon color="var(--mui-palette-text-disabled)" size={12} />
                <Typography color="text.secondary" sx={{ fontSize: '0.75rem' }} variant="caption">
                  {[row.original.city, row.original.state, row.original.zip_code].filter(Boolean).join(', ')}
                </Typography>
              </Stack>
            )}
          </Box>
        ),
      },
      {
        accessorKey: 'unit_number',
        header: 'Unit #',
        size: 100,
        enableSorting: true,
        enableColumnFilter: true,
        filterVariant: 'text',
        accessorFn: (row) => row.unit_number ?? '',
        Cell: ({ row }) => (
          <Typography color={row.original.unit_number ? 'text.primary' : 'text.disabled'} sx={{ fontSize: '0.875rem' }} variant="body2">
            {row.original.unit_number || '—'}
          </Typography>
        ),
      },
      {
        accessorKey: 'current_residents',
        header: 'Residents',
        size: 200,
        enableSorting: true,
        enableColumnFilter: true,
        filterVariant: 'select',
        filterSelectOptions: [
          { text: 'All', value: '' },
          { text: 'Occupied', value: 'occupied' },
          { text: 'Vacant', value: 'vacant' },
        ],
        accessorFn: (row) => {
          const residents = row.current_residents && row.current_residents.length > 0 ? row.current_residents : (row.owners ?? []);
          return residents.length === 0 ? 'vacant' : 'occupied';
        },
        Cell: ({ row }) => {
          const residents = row.original.current_residents && row.original.current_residents.length > 0
            ? row.original.current_residents
            : (row.original.owners ?? []);

          if (residents.length === 0) {
            return (
              <Chip
                icon={<HouseLineIcon size={14} />}
                label="Vacant"
                size="small"
                sx={{
                  height: 24,
                  fontSize: '0.6875rem',
                  fontWeight: 600,
                  bgcolor: '#EF444418',
                  color: '#EF4444',
                  border: '1px solid #EF444440',
                  '& .MuiChip-icon': { color: '#EF4444' },
                }}
              />
            );
          }

          return (
            <Stack direction="row" spacing={1} sx={{ alignItems: 'center' }}>
              <AvatarGroup max={3} sx={{ justifyContent: 'flex-start' }}>
                {residents.slice(0, 3).map((r: any) => {
                  const label = `${r.first_name ?? ''} ${r.last_name ?? ''}`.trim() || r.email || 'User';
                  const initials = ((r.first_name?.[0] ?? '') + (r.last_name?.[0] ?? '')).toUpperCase();
                  return (
                    <Tooltip key={r.id} title={label}>
                      <Avatar alt={label} src={r.avatar ?? undefined} sx={{ width: 24, height: 24, fontSize: '0.625rem', bgcolor: 'primary.main' }}>
                        {!r.avatar ? (initials || label[0].toUpperCase()) : null}
                      </Avatar>
                    </Tooltip>
                  );
                })}
              </AvatarGroup>
              <Typography color="text.secondary" sx={{ fontSize: '0.8125rem' }}>
                {residents.length} resident{residents.length !== 1 ? 's' : ''}
              </Typography>
            </Stack>
          );
        },
      },
      {
        accessorKey: 'is_rental',
        header: 'Type',
        size: 100,
        enableSorting: true,
        enableColumnFilter: true,
        filterVariant: 'select',
        filterSelectOptions: [
          { text: 'All', value: '' },
          { text: 'Rental', value: 'true' },
          { text: 'Owned', value: 'false' },
        ],
        accessorFn: (row) => (row.is_rental ? 'Rental' : 'Owned'),
        Cell: ({ row }) =>
          row.original.is_rental ? (
            <Box
              sx={{
                display: 'inline-flex',
                alignItems: 'center',
                px: 1,
                py: '2px',
                borderRadius: 1,
                bgcolor: '#6366F118',
                border: '1px solid #6366F140',
              }}
            >
              <Typography sx={{ fontSize: '0.6875rem', fontWeight: 700, color: '#6366F1', lineHeight: 1.4 }}>
                Rental
              </Typography>
            </Box>
          ) : (
            <Typography color="text.secondary" sx={{ fontSize: '0.875rem' }} variant="body2">
              Owned
            </Typography>
          ),
      },
      {
        accessorKey: 'created_at',
        header: 'Created',
        size: 120,
        enableSorting: true,
        enableColumnFilter: false,
        accessorFn: (row) => dayjs(row.created_at).format('MMM D, YYYY'),
        Cell: ({ row }) => (
          <Typography color="text.secondary" sx={{ fontSize: '0.875rem' }} variant="body2">
            {dayjs(row.original.created_at).format('MMM D, YYYY')}
          </Typography>
        ),
      },
    ],
    []
  );

  const table = useMaterialReactTable<Unit>({
    columns,
    data: rows,
    initialState: {
      showGlobalFilter: true,
      showColumnFilters: false,
      sorting: [{ id: 'address', desc: false }],
      density: 'compact',
    },
    state: { isLoading: loading },
    muiSearchTextFieldProps: {
      size: 'small',
      variant: 'outlined',
      placeholder: 'Search units…',
    },
    enableRowActions: false,
    enableRowSelection: false,
    enablePagination: true,
    enableStickyHeader: true,
    muiTableContainerProps: { sx: { minHeight: 400 } },
    muiTablePaperProps: { elevation: 0, sx: { border: 'none', borderRadius: 0 } },
    muiTableProps: { sx: { tableLayout: 'fixed' } },
    muiTableHeadProps: {
      sx: {
        '& tr th': {
          bgcolor: 'background.default',
          borderBottom: '1px solid',
          borderColor: 'divider',
          color: 'text.secondary',
          fontSize: '0.6875rem',
          fontWeight: 700,
          letterSpacing: '0.06em',
          textTransform: 'uppercase',
          py: 1.5,
        },
      },
    },
    muiTableBodyProps: {
      sx: {
        '& tr td': {
          bgcolor: 'background.paper',
          borderBottom: '1px solid',
          borderColor: 'divider',
          fontSize: '0.875rem',
          py: 1.5,
        },
      },
    },
    muiBottomToolbarProps: {
      sx: {
        bgcolor: 'background.paper',
        borderTop: '1px solid',
        borderColor: 'divider',
        boxShadow: 'none',
      },
    },
    muiTableBodyRowProps: ({ row }) => ({
      onClick: () => {
        navigate(paths.units.details(row.original.uuid ?? row.original.id?.toString() ?? ''));
      },
      sx: {
        cursor: 'pointer',
        '&:hover td': { bgcolor: 'action.hover' },
      },
    }),
    renderTopToolbar: ({ table }) => {
      const occupiedCount = rows.filter((u) => (u.current_residents?.length ?? 0) > 0 || (u.owners?.length ?? 0) > 0).length;
      const vacantCount = rows.length - occupiedCount;

      return (
        <Box
          sx={{
            bgcolor: 'background.paper',
            borderBottom: '1px solid',
            borderColor: 'divider',
            display: 'flex',
            gap: 1,
            px: 2,
            py: 1.5,
            justifyContent: 'space-between',
            alignItems: 'center',
          }}
        >
          <Stack direction="row" spacing={1} sx={{ alignItems: 'center', flexWrap: 'wrap' }}>
            <MRT_GlobalFilterTextField table={table} />
            <MRT_ToggleFiltersButton table={table} />
            <MRT_ShowHideColumnsButton table={table} />
            <MRT_ToggleDensePaddingButton table={table} />

            <Box
              sx={{
                display: 'flex',
                alignItems: 'center',
                gap: 1.5,
                ml: 1,
                pl: 1.5,
                borderLeft: '1px solid',
                borderColor: 'divider',
              }}
            >
              <Typography color="text.disabled" sx={{ fontSize: '0.6875rem', fontWeight: 700, letterSpacing: '0.06em', textTransform: 'uppercase' }}>
                {occupiedCount} occupied
              </Typography>
              <Typography color="text.disabled" sx={{ fontSize: '0.6875rem' }}>·</Typography>
              <Typography color="text.disabled" sx={{ fontSize: '0.6875rem', fontWeight: 700, letterSpacing: '0.06em', textTransform: 'uppercase' }}>
                {vacantCount} vacant
              </Typography>
            </Box>
          </Stack>

          <Button
            onClick={(e) => setExportMenuAnchor(e.currentTarget)}
            size="small"
            startIcon={<DownloadIcon size={16} />}
            sx={{ fontWeight: 500 }}
            variant="outlined"
          >
            Export
          </Button>
        </Box>
      );
    },
  });

  return (
    <>
      <MaterialReactTable table={table} />
      <Menu anchorEl={exportMenuAnchor} onClose={() => setExportMenuAnchor(null)} open={Boolean(exportMenuAnchor)}>
        <MenuItem onClick={exportToCSV}>
          <ListItemIcon><CsvIcon size={18} /></ListItemIcon>
          <ListItemText>Export to CSV</ListItemText>
        </MenuItem>
        <MenuItem onClick={exportToExcel}>
          <ListItemIcon><ExcelIcon size={18} /></ListItemIcon>
          <ListItemText>Export to Excel</ListItemText>
        </MenuItem>
      </Menu>
    </>
  );
}
